/**
 * Lógica de la base de datos del oficial (cargas, vínculo de columnas, filtros de públicos). Pura.
 *
 * El backend es el que decide (`importacion.util.ts`, `publico.util.ts`): lo de acá repite sus reglas
 * para avisar ANTES de mandar (un archivo de 6 MB que igual va a rebotar, un filtro a medio armar que no
 * vale la pena contar) y para mostrar las cosas con los textos literales de SPEC-BD §6-§7. Si una regla
 * cambia allá, se cambia acá.
 */

import type {
  CampoCatalogo, CargaPublica, CondicionPublico, ConsentimientoContacto, DeclaracionConsentimiento, DestinoColumna, ErrorFila,
  FiltroPublico, FormatoCarga, MotivoErrorFila, OpCampo, TipoCampo, TipoCondicion, VinculoColumna,
} from "../types.base";
import { num } from "../../campana-nueva/utils/format";

/* ── Archivo ──────────────────────────────────────────────────────────────────────────────── */

/** El formato sale de la extensión, igual que en el backend: un .xls viejo empieza con `0M8R` y no se adivina por el contenido. */
export function tipoDeArchivo(nombre: string): FormatoCarga | null {
  const m = /\.([a-z0-9]+)$/i.exec(String(nombre || "").trim());
  const ext = m ? m[1].toLowerCase() : "";
  return ext === "xlsx" || ext === "xls" || ext === "csv" ? ext : null;
}

/**
 * Tope en bytes del archivo en disco. El backend corta en 8.000.000 caracteres de `content` (SPEC-BD B-1):
 * un Excel viaja en base64, que infla un 33 % (5.900.000 bytes → 7.866.668 caracteres); un CSV viaja
 * como texto, y cada byte da a lo sumo un carácter.
 */
export function topeBytes(tipo: FormatoCarga): number {
  return tipo === "csv" ? 8_000_000 : 5_900_000;
}

/** En el borde (exactamente el tope) todavía entra. */
export function excedeTope(bytes: number, tipo: FormatoCarga): boolean {
  return !(Number(bytes) <= topeBytes(tipo));
}

/**
 * «5,9 MB» (megas decimales, como los topes). Redondea para ARRIBA a un decimal: un archivo de
 * 5.900.001 bytes dice «6 MB» y no «5,9 MB», que es el máximo y haría creer que entra.
 */
export function mb(bytes: number): string {
  const n = Number(bytes);
  const decimas = Number.isFinite(n) && n > 0 ? Math.ceil(n / 100_000) : 0;
  const entero = Math.floor(decimas / 10);
  const resto = decimas % 10;
  return `${entero}${resto ? `,${resto}` : ""} MB`;
}

/** Chequeo local antes de subir (SPEC-BD §6.2, paso 1): la extensión y el tamaño, con los textos literales. */
export function validarArchivoLocal(nombre: string, bytes: number): { ok: true; tipo: FormatoCarga } | { ok: false; error: string } {
  const tipo = tipoDeArchivo(nombre);
  if (!tipo) return { ok: false, error: "Ese archivo no es un Excel ni un CSV." };
  if (excedeTope(bytes, tipo)) {
    return { ok: false, error: `El archivo pesa ${mb(bytes)}: el máximo es ${mb(topeBytes(tipo))}. Sacá columnas que no uses o partilo en dos.` };
  }
  return { ok: true, tipo };
}

/** El nombre de la carga por defecto: el del archivo sin extensión, hasta 120 caracteres. */
export function nombreDeCarga(nombreArchivo: string): string {
  return String(nombreArchivo || "").trim().replace(/\.[a-z0-9]+$/i, "").trim().slice(0, 120);
}

/* ── Texto y etiquetas ────────────────────────────────────────────────────────────────────── */

/** Minúsculas, sin tildes (NFD), espacios colapsados, trim. Igual que el backend: «Feria 2026» y «feria  2026» son la misma etiqueta. */
export function normalizarTexto(s: string): string {
  return String(s ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
}

export const MAX_ETIQUETAS_CARGA = 10;
export const MAX_LARGO_ETIQUETA = 40;

/**
 * Suma lo que el operador escribió (una o varias separadas por «,» «;» o «|») a las etiquetas que ya
 * tiene. Una repetida (misma clave, sin importar mayúsculas ni tildes) no se suma. Devuelve el error
 * para mostrar si alguna no entró: más de 40 caracteres o pasarse del tope.
 */
export function agregarEtiquetas(actuales: string[], entrada: string, max = MAX_ETIQUETAS_CARGA): { etiquetas: string[]; error: string } {
  const etiquetas = [...(actuales || [])];
  const claves = new Set(etiquetas.map(normalizarTexto));
  let error = "";
  for (const parte of String(entrada ?? "").split(/[,;|]/)) {
    const texto = parte.replace(/\s+/g, " ").trim();
    if (!texto) continue;
    if (texto.length > MAX_LARGO_ETIQUETA) { error = `Cada etiqueta puede tener hasta ${MAX_LARGO_ETIQUETA} caracteres.`; continue; }
    const clave = normalizarTexto(texto);
    if (claves.has(clave)) continue;
    if (etiquetas.length >= max) { error = `Hasta ${max} etiquetas.`; continue; }
    claves.add(clave);
    etiquetas.push(texto);
  }
  return { etiquetas, error };
}

/* ── Vínculo de columnas ──────────────────────────────────────────────────────────────────── */

/** Opciones del `select` «Qué es», en el orden de la pantalla. */
export const DESTINOS: readonly { destino: DestinoColumna; label: string }[] = [
  { destino: "telefono", label: "Teléfono" },
  { destino: "nombre", label: "Nombre" },
  { destino: "apellido", label: "Apellido" },
  { destino: "email", label: "Email" },
  { destino: "consentimiento", label: "Consentimiento (sí/no)" },
  { destino: "fechaConsentimiento", label: "Fecha del consentimiento" },
  { destino: "fuenteConsentimiento", label: "Fuente del consentimiento" },
  { destino: "etiquetas", label: "Etiquetas" },
  { destino: "sucursal", label: "Sucursal" },
  { destino: "campo", label: "Campo personalizado" },
  { destino: "ignorar", label: "No importar" },
];
export function labelDestino(d: DestinoColumna): string {
  return DESTINOS.find((x) => x.destino === d)?.label ?? d;
}

export const TIPOS_CAMPO: readonly { tipo: TipoCampo; label: string }[] = [
  { tipo: "texto", label: "Texto" }, { tipo: "numero", label: "Número" }, { tipo: "fecha", label: "Fecha" },
];
const NOMBRE_TIPO: Record<TipoCampo, string> = { texto: "texto", numero: "número", fecha: "fecha" };

/** Claves que no puede tomar un campo personalizado: son datos fijos o variables de siempre (igual que el backend). */
export const CLAVES_RESERVADAS: ReadonlySet<string> = new Set([
  "telefono", "nombre", "apellido", "email", "sucursal", "etiquetas", "consentimiento",
  "marca", "cupon", "grupo", "oferta", "vence", "primernombre",
]);
const CLAVE_VALIDA = /^[a-z][a-z0-9_]{0,39}$/;
export const MAX_CAMPOS_MARCA = 50;
export const MAX_LARGO_ETIQUETA_CAMPO = 60;

function claveBase(texto: string): string {
  let base = normalizarTexto(texto).replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  if (!base) base = "campo";
  if (!/^[a-z]/.test(base)) base = `c_${base}`;
  return base.slice(0, 40).replace(/_+$/, "");
}

/**
 * «Fecha de nacimiento» → «fecha_de_nacimiento». Minúsculas sin tildes, [a-z0-9_], empieza con letra (si no,
 * «c_» adelante), ≤ 40. Una reservada o ya usada sigue con _2, _3… Misma regla que el backend: es lo que
 * la pantalla muestra como «se guarda como …» y lo que viaja en el vínculo.
 */
export function claveDeColumna(texto: string, usadas: ReadonlySet<string>): string {
  const base = claveBase(texto);
  let clave = base;
  for (let n = 2; usadas.has(clave) || CLAVES_RESERVADAS.has(clave); n++) {
    const suf = `_${n}`;
    clave = `${base.slice(0, 40 - suf.length).replace(/_+$/, "")}${suf}`;
  }
  return clave;
}

/**
 * El campo personalizado de una columna a partir de su nombre visible. Si la clave ya existe en el
 * catálogo de la marca (y ninguna otra columna la tomó), se reusa con su tipo: `existente`, y el tipo no
 * se puede cambiar. Si no, es un campo nuevo con una clave que no choca con el catálogo ni con las otras
 * columnas del vínculo.
 */
export function armarCampo(
  etiqueta: string, tipo: TipoCampo, catalogo: CampoCatalogo[], otrasClaves: ReadonlySet<string>,
): NonNullable<VinculoColumna["campo"]> {
  const texto = String(etiqueta ?? "").replace(/\s+/g, " ").trim();
  const base = claveBase(texto);
  const delCatalogo = texto ? (catalogo || []).find((c) => c.clave === base) : undefined;
  if (delCatalogo && !otrasClaves.has(base)) return { clave: base, etiqueta: texto, tipo: delCatalogo.tipo, existente: true };
  const usadas = new Set([...otrasClaves, ...(catalogo || []).map((c) => c.clave)]);
  return { clave: claveDeColumna(texto, usadas), etiqueta: texto, tipo, existente: false };
}

const clavesDeOtras = (v: VinculoColumna[], columna: string) =>
  new Set(v.filter((x) => x.columna !== columna && x.destino === "campo" && x.campo?.clave).map((x) => x.campo!.clave));

/**
 * El vínculo con el que arranca el paso 2: el guardado en la carga si hay (retomar un borrador), si no la
 * sugerencia del backend. Queda una entrada por columna del archivo, en su orden; una columna que ni el
 * guardado ni la sugerencia nombran va a «No importar», y lo que nombra una columna que ya no está se descarta.
 */
export function vinculoInicial(columnas: string[], sugerencia: VinculoColumna[], guardado?: VinculoColumna[] | null): VinculoColumna[] {
  const fuente = guardado && guardado.length ? guardado : sugerencia || [];
  const porColumna = new Map<string, VinculoColumna>();
  for (const v of fuente) if (v && !porColumna.has(v.columna)) porColumna.set(v.columna, v);
  return (columnas || []).map((columna) => {
    const v = porColumna.get(columna);
    if (!v) return { columna, destino: "ignorar" as const };
    return v.destino === "campo" && v.campo ? { columna, destino: "campo" as const, campo: { ...v.campo } } : { columna, destino: v.destino };
  });
}

/**
 * Cambia el destino de una columna. Al pasar a «Campo personalizado» arma el campo con el nombre de la
 * columna (o el `titulo` si viene, y vacío si la columna no tenía nombre: el operador lo tiene que escribir)
 * y el tipo que sugirió el backend para esa columna, si lo sugirió; si no, texto.
 */
export function cambiarDestino(
  v: VinculoColumna[], columna: string, destino: DestinoColumna,
  o: { catalogo: CampoCatalogo[]; sugerencia?: VinculoColumna[]; titulo?: string },
): VinculoColumna[] {
  return v.map((x) => {
    if (x.columna !== columna) return x;
    if (destino !== "campo") return { columna, destino };
    if (x.destino === "campo" && x.campo) return x;
    const sugerido = (o.sugerencia || []).find((s) => s.columna === columna && s.destino === "campo")?.campo;
    const nombre = o.titulo ?? (columna.startsWith("__EMPTY") ? "" : columna);
    return { columna, destino, campo: armarCampo(nombre, sugerido?.tipo ?? "texto", o.catalogo, clavesDeOtras(v, columna)) };
  });
}

/** Cambia el nombre visible o el tipo del campo de una columna. La clave sigue al nombre; un campo existente conserva su tipo. */
export function cambiarCampo(
  v: VinculoColumna[], columna: string, cambios: { etiqueta?: string; tipo?: TipoCampo }, catalogo: CampoCatalogo[],
): VinculoColumna[] {
  return v.map((x) => {
    if (x.columna !== columna || x.destino !== "campo" || !x.campo) return x;
    const etiqueta = cambios.etiqueta ?? x.campo.etiqueta;
    const tipo = cambios.tipo ?? x.campo.tipo;
    return { ...x, campo: armarCampo(etiqueta, tipo, catalogo, clavesDeOtras(v, columna)) };
  });
}

export interface ValidacionVinculo {
  ok: boolean;
  /** Para mostrar abajo del «Siguiente» deshabilitado (el primero es el motivo). Textos de SPEC-BD §7.3. */
  errores: string[];
  /** Junto al `select` de cada columna. */
  porColumna: Record<string, string>;
}

/**
 * La misma regla que `validarVinculo` del backend: exactamente un Teléfono; un solo destino por tipo, salvo
 * Campo personalizado y No importar; cada campo con nombre (≤ 60) y una clave válida, no reservada ni
 * repetida; un campo existente con su mismo tipo; y no más de 50 campos entre los de la marca y los nuevos.
 */
export function validarVinculoLocal(v: VinculoColumna[], catalogo: CampoCatalogo[] = []): ValidacionVinculo {
  const errores: string[] = [];
  const porColumna: Record<string, string> = {};
  const lista = Array.isArray(v) ? v : [];

  const porDestino = new Map<DestinoColumna, string[]>();
  for (const x of lista) {
    if (!x || x.destino === "campo" || x.destino === "ignorar") continue;
    porDestino.set(x.destino, [...(porDestino.get(x.destino) || []), x.columna]);
  }
  if (!porDestino.get("telefono")?.length) errores.push("Falta elegir la columna del teléfono.");
  for (const [destino, columnas] of porDestino) {
    if (columnas.length < 2) continue;
    errores.push(`Elegiste dos columnas como ${labelDestino(destino)}: ${columnas[0]} y ${columnas[1]}.`);
    for (const c of columnas.slice(1)) porColumna[c] = `Ya elegiste otra columna como ${labelDestino(destino)}`;
  }

  const catalogoPorClave = new Map((catalogo || []).map((c) => [c.clave, c]));
  const vistas = new Set<string>();
  const nuevas = new Set<string>();
  for (const x of lista) {
    if (!x || x.destino !== "campo") continue;
    const c = x.campo;
    const marcar = (texto: string) => { if (!porColumna[x.columna]) porColumna[x.columna] = texto; if (!errores.includes(texto)) errores.push(texto); };
    if (!c || !String(c.etiqueta || "").trim()) { marcar("Escribí el nombre del campo."); continue; }
    if (c.etiqueta.length > MAX_LARGO_ETIQUETA_CAMPO) { marcar(`El nombre del campo puede tener hasta ${MAX_LARGO_ETIQUETA_CAMPO} caracteres.`); continue; }
    if (!CLAVE_VALIDA.test(c.clave) || CLAVES_RESERVADAS.has(c.clave)) { marcar(`El campo "${c.etiqueta}" no se puede guardar con ese nombre: elegí otro.`); continue; }
    if (vistas.has(c.clave)) { marcar(`Hay dos columnas cargadas como el campo "${c.etiqueta}".`); continue; }
    vistas.add(c.clave);
    const existente = catalogoPorClave.get(c.clave);
    if (existente && existente.tipo !== c.tipo) {
      marcar(`El campo "${existente.etiqueta}" ya existe como ${NOMBRE_TIPO[existente.tipo]}: no se puede cargar como ${NOMBRE_TIPO[c.tipo] ?? c.tipo}.`);
      continue;
    }
    if (!existente) nuevas.add(c.clave);
  }
  if (catalogoPorClave.size + nuevas.size > MAX_CAMPOS_MARCA) errores.push(`Pasarías los ${MAX_CAMPOS_MARCA} campos personalizados de la marca.`);

  return { ok: errores.length === 0, errores, porColumna };
}

/* ── Consentimiento ───────────────────────────────────────────────────────────────────────── */

const DIA = /^(\d{4})-(\d{2})-(\d{2})$/;
/** "YYYY-MM-DD" de un día que existe (un 31/02 no pasa). */
export function esDiaValido(s: unknown): s is string {
  const m = typeof s === "string" ? DIA.exec(s) : null;
  if (!m) return false;
  const [a, me, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const f = new Date(Date.UTC(a, me - 1, d));
  return f.getUTCFullYear() === a && f.getUTCMonth() === me - 1 && f.getUTCDate() === d;
}
/** El día de hoy en la zona del navegador, como "YYYY-MM-DD" (lo que muestra un `<input type="date">`). */
export function hoyLocal(ahora: Date = new Date()): string {
  const dos = (n: number) => String(n).padStart(2, "0");
  return `${ahora.getFullYear()}-${dos(ahora.getMonth() + 1)}-${dos(ahora.getDate())}`;
}

export const TEXTO_DECLARACION_INCOMPLETA =
  "Para declarar el consentimiento de todo el archivo, completá cómo lo dieron y cuándo, y marcá que tenés constancia.";

/**
 * «Todo este archivo tiene consentimiento» (paso 3). Apagado → null (no hay declaración). Encendido exige lo
 * mismo que el backend: cómo (1..100), cuándo (un día real entre 2000-01-01 y hoy) y la casilla de
 * constancia. Una declaración a medias no se manda: el backend la rechazaría con BAD_INPUT.
 */
export function validarDeclaracionLocal(
  d: { activa: boolean; fuente: string; fecha: string; constancia: boolean }, hoy: string,
): { ok: true; declaracion: DeclaracionConsentimiento | null } | { ok: false; error: string } {
  if (!d?.activa) return { ok: true, declaracion: null };
  const fuente = String(d.fuente ?? "").trim();
  const fecha = String(d.fecha ?? "");
  const ok = fuente.length >= 1 && fuente.length <= 100 && esDiaValido(fecha) && fecha >= "2000-01-01" && fecha <= hoy && d.constancia === true;
  return ok ? { ok: true, declaracion: { fuente, fecha, constancia: true } } : { ok: false, error: TEXTO_DECLARACION_INCOMPLETA };
}

/**
 * «Registrar consentimiento» / «Quitar consentimiento» de la ficha (SPEC-BD §4.3). Registrar: cómo (1..100),
 * cuándo (≤ hoy), constancia y motivo (1..500). Quitar: sólo el motivo.
 */
export function validarConsentimientoManual(
  c: { optIn: boolean; fuente?: string; fecha?: string; constancia?: boolean; motivo: string }, hoy: string,
): { ok: true } | { ok: false; error: string } {
  const motivo = String(c?.motivo ?? "").trim();
  const motivoOk = motivo.length >= 1 && motivo.length <= 500;
  if (!c?.optIn) return motivoOk ? { ok: true } : { ok: false, error: "Escribí el motivo del cambio." };
  const fuente = String(c.fuente ?? "").trim();
  const fecha = String(c.fecha ?? "");
  const ok = motivoOk && fuente.length >= 1 && fuente.length <= 100 && esDiaValido(fecha) && fecha <= hoy && c.constancia === true;
  return ok ? { ok: true }
    : { ok: false, error: "Contá cómo y cuándo dio el consentimiento, marcá que tenés constancia y escribí el motivo del cambio." };
}

/**
 * Chip del consentimiento. Acepta el `optIn` de la ficha o el valor de una fila. Cualquier cosa que no sea
 * un sí explícito NO se muestra como «Sí» (fail-closed, D3).
 */
export function textoConsentimiento(optIn: boolean | null | undefined | ConsentimientoContacto): { valor: ConsentimientoContacto; label: string; cls: string } {
  if (optIn === true || optIn === "si") return { valor: "si", label: "Sí", cls: "bg-emerald-50 text-emerald-700" };
  if (optIn === false || optIn === "no") return { valor: "no", label: "Dijo que no", cls: "bg-red-50 text-red-600" };
  return { valor: "sin_dato", label: "Sin dato", cls: "bg-slate-100 text-slate-600" };
}

/* ── Errores de las filas ─────────────────────────────────────────────────────────────────── */

/** SPEC-BD §7.4. `columna` y `valor` completan los textos que los usan. */
export function textoMotivoError(m: MotivoErrorFila, e: { columna?: string; valor?: string } = {}): string {
  switch (m) {
    case "telefono_vacio": return "Sin teléfono";
    case "telefono_invalido": return "El teléfono no es válido";
    case "telefono_es_email": return "En la columna del teléfono hay un email";
    case "numero_eeuu": return "Número de EE. UU.: Meta no entrega promociones ahí";
    case "email_invalido": return "El email no es válido: se importó sin email";
    case "fecha_consentimiento_invalida": return "No se entendió la fecha del consentimiento (va día/mes/año)";
    case "consentimiento_no_reconocido": return "No se entendió el consentimiento: queda sin consentimiento";
    case "valor_recortado": return "Tenía más de 500 caracteres y se recortó";
    case "numero_invalido": return `«${e.columna ?? ""}» no es un número: se guardó como texto`;
    case "fecha_invalida": return `«${e.columna ?? ""}» no es una fecha (va día/mes/año): se guardó como texto`;
    case "duplicado": return `Teléfono repetido en el archivo: se unió con la fila ${e.valor ?? ""}`.trim();
    case "etiquetas_de_mas": return "Más de 30 etiquetas: se guardaron las primeras";
    case "si_con_no_contactar": return "Está en no contactar: el «sí» del archivo no la vuelve a habilitar";
    default: return `Problema sin identificar (${String(m)})`;
  }
}

/** La tabla del paso 4 es una muestra: con 10 contactos y 5 filas, «Así quedan 5 contactos» se leía como el total. */
export function tituloMuestra(enMuestra: number, total: number): string {
  if (enMuestra >= total) return enMuestra === 1 ? "Así queda el contacto" : `Así quedan los ${num(enMuestra)} contactos`;
  return `Así quedan los primeros ${num(enMuestra)} de ${num(total)} contactos`;
}

/**
 * Una celda del CSV: entre comillas si tiene «;», comillas o saltos de línea, con las comillas duplicadas.
 * Si empieza con = + - @ (o un tab o un retorno) va con un apóstrofo adelante: Excel la tomaría como
 * fórmula. Un valor enmascarado como «+54 9 ••• 1234» daría #¿NOMBRE?, y un nombre del archivo que empiece
 * con «=» se ejecutaría al abrir el detalle.
 */
function celdaCsv(v: unknown): string {
  let s = String(v ?? "");
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[;"\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * El detalle de errores de una carga para «Descargar el detalle (CSV)»: BOM UTF-8 (sin él, Excel en
 * Windows lee las tildes mal), separador «;» (el de Excel en español), encabezado `Fila;Columna;Problema;Valor`.
 */
export function erroresACsv(errores: ErrorFila[], textos: (e: ErrorFila) => string = (e) => textoMotivoError(e.motivo, e)): string {
  const filas = [["Fila", "Columna", "Problema", "Valor"], ...(errores || []).map((e) => [e.fila, e.columna, textos(e), e.valor])];
  return `\uFEFF${filas.map((f) => f.map(celdaCsv).join(";")).join("\r\n")}\r\n`;
}

/* ── Cargas ───────────────────────────────────────────────────────────────────────────────── */

/**
 * La acción de la zona de peligro del detalle de una carga. Una fallida que ya escribió contactos no se
 * puede descartar (el backend la rechaza: sus `cargaIds` quedarían colgados), así que va «Borrar los
 * datos de esta carga», la única forma de sacar esos contactos en bloque. En proceso o ya borrada, nada.
 */
export function accionPeligroCarga(c: Pick<CargaPublica, "estado" | "contactosVigentes">): "borrar_datos" | "descartar" | null {
  if (c.estado === "importada") return "borrar_datos";
  if (c.estado === "fallida") return (Number(c.contactosVigentes) || 0) > 0 ? "borrar_datos" : "descartar";
  if (c.estado === "borrador") return "descartar";
  return null;
}

/** «Contactos» de la lista de cargas: los que escribió la importación, o los que siguen teniendo esa
 *  carga si se borró o si falló a mitad (la fallida no tiene resultado, pero sí contactos). */
export function contactosDeCarga(
  c: Pick<CargaPublica, "estado" | "contactosVigentes"> & { resultado: Pick<NonNullable<CargaPublica["resultado"]>, "nuevos" | "actualizados"> | null },
): number | null {
  if (c.estado === "eliminada" || c.estado === "eliminando" || c.estado === "fallida") return Number(c.contactosVigentes) || 0;
  if (c.resultado) return c.resultado.nuevos + c.resultado.actualizados;
  return null;
}

/* ── Filtros de públicos ──────────────────────────────────────────────────────────────────── */

export const MAX_CONDICIONES = 20;
const MAX_VALORES = 50;

/** Operadores de un campo personalizado según su tipo, con los textos de la pantalla (SPEC-BD §6.4). */
export function opsDeCampo(tipo: TipoCampo): { op: OpCampo; label: string; necesitaValor: boolean }[] {
  const tiene = [{ op: "existe" as const, label: "tiene dato", necesitaValor: false }, { op: "no_existe" as const, label: "no tiene dato", necesitaValor: false }];
  if (tipo === "numero") {
    return [{ op: "igual", label: "es igual a", necesitaValor: true }, { op: "mayor", label: "es mayor que", necesitaValor: true },
      { op: "menor", label: "es menor que", necesitaValor: true }, ...tiene];
  }
  if (tipo === "fecha") {
    return [{ op: "igual", label: "es el", necesitaValor: true }, { op: "mayor", label: "es después del", necesitaValor: true },
      { op: "menor", label: "es antes del", necesitaValor: true }, ...tiene];
  }
  return [{ op: "igual", label: "es igual a", necesitaValor: true }, { op: "contiene", label: "contiene", necesitaValor: true }, ...tiene];
}

/** Operadores de las condiciones de lista (etiquetas, carga de origen, sucursal), con los textos de la pantalla. */
export function opsDeLista(tipo: "etiquetas" | "carga" | "sucursal"): { op: "alguna" | "todas" | "ninguna"; label: string }[] {
  if (tipo === "etiquetas") return [{ op: "alguna", label: "incluye alguna de" }, { op: "todas", label: "incluye todas" }, { op: "ninguna", label: "no incluye ninguna de" }];
  if (tipo === "carga") return [{ op: "alguna", label: "viene de alguna de" }, { op: "ninguna", label: "no viene de ninguna de" }];
  return [{ op: "alguna", label: "es alguna de" }, { op: "ninguna", label: "no es ninguna de" }];
}

/** Las opciones del menú «Agregar condición», en su orden. */
export const TIPOS_CONDICION: readonly { tipo: TipoCondicion; label: string }[] = [
  { tipo: "etiquetas", label: "Etiquetas" }, { tipo: "campo", label: "Campo personalizado" }, { tipo: "carga", label: "Carga de origen" },
  { tipo: "sucursal", label: "Sucursal" }, { tipo: "alta", label: "Fecha de alta" }, { tipo: "email", label: "Email" },
  { tipo: "consentimiento", label: "Consentimiento" },
];

/** La condición recién agregada, antes de que el operador la complete. Las que necesitan un valor quedan inválidas hasta tenerlo. */
export function condicionVacia(tipo: TipoCondicion): CondicionPublico {
  switch (tipo) {
    case "etiquetas": return { tipo, op: "alguna", valores: [] };
    case "campo": return { tipo, clave: "", op: "existe" };
    case "carga": return { tipo, op: "alguna", valores: [] };
    case "sucursal": return { tipo, op: "alguna", valores: [] };
    case "alta": return { tipo };
    case "email": return { tipo, tiene: true };
    case "consentimiento": return { tipo, valores: ["si"] };
  }
}

/** Nombre corto del operador para los errores («"mayor que" necesita un número»). */
function nombreOp(op: OpCampo, tipo: TipoCampo): string {
  if (op === "igual") return tipo === "fecha" ? "es el" : "igual a";
  if (op === "mayor") return tipo === "fecha" ? "después del" : "mayor que";
  if (op === "menor") return tipo === "fecha" ? "antes del" : "menor que";
  if (op === "contiene") return "contiene";
  return op === "existe" ? "tiene dato" : "no tiene dato";
}

function detalleCondicion(c: CondicionPublico, catalogo: CampoCatalogo[]): string | null {
  const x = c as unknown as Record<string, unknown>;
  const lista = (vacia: string): string | null => {
    const valores = x.valores;
    if (!Array.isArray(valores) || !valores.length) return vacia;
    if (valores.length > MAX_VALORES) return `hay más de ${MAX_VALORES} valores en una condición`;
    if (valores.some((v) => typeof v !== "string" || !v.trim() || v.length > 100)) return "hay un valor que no se entiende";
    return null;
  };
  switch (c?.tipo) {
    case "etiquetas":
      if (!["alguna", "todas", "ninguna"].includes(String(x.op))) return "la condición de etiquetas no tiene un operador válido";
      return lista("elegí al menos una etiqueta");
    case "carga":
    case "sucursal":
      if (!["alguna", "ninguna"].includes(String(x.op))) return `la condición de ${c.tipo === "carga" ? "carga de origen" : "sucursal"} no tiene un operador válido`;
      return lista(c.tipo === "carga" ? "elegí al menos una carga" : "elegí al menos una sucursal");
    case "campo": {
      if (typeof c.clave !== "string" || !c.clave) return "elegí un campo";
      const campo = (catalogo || []).find((k) => k.clave === c.clave);
      if (!campo) return `el campo "${c.clave}" no existe`;
      const op = opsDeCampo(campo.tipo).find((o) => o.op === c.op);
      if (!op) return `"${String(c.op)}" no se puede usar con un campo de ${NOMBRE_TIPO[campo.tipo]}`;
      if (!op.necesitaValor) return null;
      if (campo.tipo === "numero") {
        return typeof c.valor === "number" && Number.isFinite(c.valor) ? null : `"${nombreOp(c.op, campo.tipo)}" necesita un número`;
      }
      if (campo.tipo === "fecha") return esDiaValido(c.valor) ? null : `"${nombreOp(c.op, campo.tipo)}" necesita una fecha`;
      return typeof c.valor === "string" && c.valor.trim() && c.valor.length <= 200 ? null : `"${nombreOp(c.op, campo.tipo)}" necesita un texto`;
    }
    case "alta": {
      const { desde, hasta } = c;
      if (desde === undefined && hasta === undefined) return "elegí al menos una fecha de alta";
      if ((desde !== undefined && !esDiaValido(desde)) || (hasta !== undefined && !esDiaValido(hasta))) return "la fecha de alta no es válida";
      if (desde !== undefined && hasta !== undefined && desde > hasta) return "la fecha de alta «desde» es posterior a «hasta»";
      return null;
    }
    case "email":
      return typeof c.tiene === "boolean" ? null : "elegí si tiene email o no";
    case "consentimiento": {
      const v = x.valores;
      if (!Array.isArray(v) || !v.length) return "elegí al menos una opción de consentimiento";
      return v.every((s) => s === "si" || s === "no" || s === "sin_dato") ? null : "hay una opción de consentimiento que no se entiende";
    }
    default:
      return "hay una condición que no se entiende";
  }
}

/**
 * Las reglas de `validarFiltro` del backend, para no pedir un conteo con un filtro a medio armar y para
 * mostrar el error arriba de las condiciones (FILTRO_INVALIDO, SPEC-BD §7.3). Los ids de carga y las
 * claves de etiqueta los valida el backend: acá no se tienen todos.
 */
export function validarFiltroLocal(f: FiltroPublico, catalogo: CampoCatalogo[]): { ok: true } | { ok: false; detalle: string; error: string } {
  const mal = (detalle: string) => ({ ok: false as const, detalle, error: `Revisá las condiciones: ${detalle}.` });
  const condiciones = f?.condiciones;
  if (!Array.isArray(condiciones)) return mal("hay una condición que no se entiende");
  if (condiciones.length > MAX_CONDICIONES) return mal(`puede haber hasta ${MAX_CONDICIONES} condiciones`);
  for (const c of condiciones) {
    const detalle = detalleCondicion(c, catalogo);
    if (detalle) return mal(detalle);
  }
  return { ok: true };
}
